import { useState } from 'react';
import { useStore } from '../state/store';
import { Button, Card, Chip, Field, Input, SegmentedControl } from '../ui';
import { MoneyInput } from '../components/MoneyInput';
import { GlossaryTerm } from '../components/GlossaryTerm';
import type { OnboardingInput } from '../db/onboarding';

// ────────────────────────────────────────────────────────────────────────────
// Welcome / onboarding screen.
//
// Shown on a fresh browser (zero profiles) instead of the seeded Baseline
// dashboard, and re-openable from the demo banner via `startOnboarding()`.
//
// The visitor picks one of two paths:
//   1. Start setup — a compact form (age, filing status, optional salary)
//      materializes `main` with the standard account catalog (zero
//      balances) and drops them on the Update page to enter real numbers.
//   2. Explore with sample data — activates the fictional `demo` profile.
//      The amber demo banner across the top makes it obvious this isn't
//      their real data; they can start over via the same banner.
//
// Deliberately NOT retirement-centric: Cairn models arbitrary financial
// situations — a home purchase, a career change, a windfall, tracking net
// worth over time — and only some visitors are also planning a retirement
// date. The copy names Cairn as a financial planner, not a retirement one.
//
// Deliberately does NOT ask for balances. Aggregating cash + brokerage +
// retirement into a single number and then dumping it into HYSA is an
// arbitrary shape imposed on the visitor's money. The Update page is the
// primitive for "enter balances per account" — onboarding hands off to it.
//
// Design discipline:
//   - Every technical term is a GlossaryTerm.
//   - No dashboard chrome (no bottom nav, no side rail) — this screen
//     stands on its own so a first-time visitor is not staring at a full
//     app around a form they haven't filled out.
//   - Two steps only: intro → form. Progressive disclosure keeps the
//     intro card readable at a glance.
// ────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Show a "Back" affordance when a profile already exists — the visitor
   *  can bail out of onboarding without losing what they have. Fresh
   *  visitors don't get this affordance (there's nowhere to go back to). */
  canDismiss: boolean;
}

export function WelcomePage({ canDismiss }: Props) {
  const { completeOnboarding, startDemo, dismissOnboarding } = useStore();
  const [step, setStep] = useState<'intro' | 'form'>('intro');
  const [form, setForm] = useState<OnboardingInput>({
    displayName: '',
    currentAge: 35,
    filingStatus: 'single',
    grossAnnualSalary: 0,
  });
  const [saving, setSaving] = useState<null | 'form' | 'demo'>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (saving) return;
    if (!form.displayName.trim()) {
      setError('Please enter a name to continue.');
      return;
    }
    if (!(form.currentAge > 0)) {
      setError('Please enter your current age to continue.');
      return;
    }
    setError(null);
    setSaving('form');
    try {
      // Land the visitor on the Update page, where they enter real balances
      // per account. Setting the hash before completeOnboarding() flips
      // needsOnboarding=false means the Shell mounts with tab='update' on
      // first render — no dashboard flash in between.
      window.location.hash = 'update';
      await completeOnboarding(form);
    } catch (e) {
      setError(`Setup failed: ${(e as Error).message}`);
      setSaving(null);
    }
  }

  async function handleDemo() {
    if (saving) return;
    setError(null);
    setSaving('demo');
    try {
      await startDemo();
    } catch (e) {
      setError(`Could not load sample data: ${(e as Error).message}`);
      setSaving(null);
    }
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-white via-white to-ink-50 dark:from-ink-900 dark:via-ink-900 dark:to-ink-800"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-8 sm:py-12">
        <header className="mb-6 flex items-center gap-3 sm:mb-8">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-600 text-xl font-black text-white shadow-md"
            aria-hidden
          >
            C
          </div>
          <div>
            <div className="text-lg font-bold text-ink-900 dark:text-ink-50">Cairn</div>
            <div className="text-xs text-ink-500 dark:text-ink-300">
              Local-first personal finance
            </div>
          </div>
        </header>

        {step === 'intro' ? (
          <IntroCard
            onStart={() => setStep('form')}
            onDemo={handleDemo}
            onDismiss={canDismiss ? dismissOnboarding : undefined}
            demoLoading={saving === 'demo'}
          />
        ) : (
          <FormCard
            form={form}
            setForm={setForm}
            onBack={() => {
              setError(null);
              setStep('intro');
            }}
            onSubmit={handleSubmit}
            saving={saving === 'form'}
          />
        )}

        {error && (
          <div className="mt-4 rounded-xl bg-neg-50 px-4 py-2 text-xs text-neg-700 dark:bg-neg-700/20 dark:text-neg-500">
            {error}
          </div>
        )}

        <div className="mt-auto pt-8 text-center text-[11px] text-ink-500 dark:text-ink-300">
          Cairn runs entirely in your browser. No accounts, no servers, no telemetry.{' '}
          Every dollar you enter stays on this device.
        </div>
      </div>
    </main>
  );
}

// ── Intro card ──────────────────────────────────────────────────────────────

function IntroCard({
  onStart,
  onDemo,
  onDismiss,
  demoLoading,
}: {
  onStart: () => void;
  onDemo: () => void;
  onDismiss?: () => void;
  demoLoading: boolean;
}) {
  return (
    <>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ink-900 dark:text-ink-50 sm:text-4xl">
          Plan your finances, privately.
        </h1>
        <p className="mt-3 text-sm text-ink-600 dark:text-ink-300 sm:text-base">
          Track your finances, project them forward, and compare "what-if"{' '}
          <GlossaryTerm term="scenario">plans</GlossaryTerm> — a home
          purchase, a career change, a retirement date, whatever life throws
          at you. All on this device.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <Chip tone="pos" leading={<span aria-hidden>🔒</span>}>Local-only</Chip>
        <Chip tone="accent" leading={<span aria-hidden>◆</span>}>
          <GlossaryTerm term="scenario">Scenario</GlossaryTerm> modeling
        </Chip>
        <Chip tone="neutral" leading={<span aria-hidden>∅</span>}>No account required</Chip>
      </div>

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Get started in about a minute
            </h2>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
              Answer a few questions — age, filing status, and (optionally)
              salary. Then enter what's in each account on the next screen.
              Everything else you can refine as you go.
            </p>
          </div>
          <Button onClick={onStart} size="lg" block>
            Start setup
          </Button>
        </div>
      </Card>

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-ink-200 dark:bg-ink-700" />
        <span className="text-[11px] uppercase tracking-wider text-ink-400 dark:text-ink-500">
          or
        </span>
        <div className="h-px flex-1 bg-ink-200 dark:bg-ink-700" />
      </div>

      <Card>
        <div className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Just curious? Explore with sample data
            </h2>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
              Load a fictional profile — an imaginary 42-year-old with a
              house, a 401(k), and six branch scenarios — to see what a
              populated Cairn looks like before entering anything of your
              own. An amber banner will make it clear it's not your data.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={onDemo}
            disabled={demoLoading}
            block
          >
            {demoLoading ? 'Loading sample…' : 'Explore with sample data'}
          </Button>
        </div>
      </Card>

      {onDismiss && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-ink-500 hover:text-accent-600 dark:text-ink-300 dark:hover:text-accent-500"
          >
            ← Back to my dashboard
          </button>
        </div>
      )}
    </>
  );
}

// ── Form card ───────────────────────────────────────────────────────────────

function FormCard({
  form,
  setForm,
  onBack,
  onSubmit,
  saving,
}: {
  form: OnboardingInput;
  setForm: (fn: (prev: OnboardingInput) => OnboardingInput) => void;
  onBack: () => void;
  onSubmit: () => void;
  saving: boolean;
}) {
  return (
    <Card>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-ink-900 dark:text-ink-50">
            About you
          </h1>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-300">
            Just enough to seed a profile. Next you'll enter today's balance
            for each account — everything else (contributions, mortgage,
            spending, life events) you can add as you go.
          </p>
        </div>

        <Field
          label="Your name"
          hint="Just for the Dashboard header — first name, household, whatever you'd like."
        >
          <Input
            type="text"
            maxLength={60}
            value={form.displayName}
            placeholder="e.g. Alex, or Alex & Sam"
            autoFocus
            onChange={e =>
              setForm(f => ({ ...f, displayName: e.target.value }))
            }
          />
        </Field>

        <Field label="Your current age" hint="Whole years.">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={120}
            value={form.currentAge || ''}
            onChange={e =>
              setForm(f => ({ ...f, currentAge: parseIntSafe(e.target.value) }))
            }
          />
        </Field>

        <Field
          label="Filing status"
          hint="Affects tax brackets, the standard deduction, and Social Security taxation."
        >
          <SegmentedControl
            value={form.filingStatus}
            options={[
              { value: 'single', label: 'Single' },
              { value: 'married', label: 'Married filing jointly' },
            ]}
            onChange={v => setForm(f => ({ ...f, filingStatus: v }))}
            block
            ariaLabel="Filing status"
          />
        </Field>

        <Field
          label="Gross annual salary (optional)"
          hint="Leave blank if you're not currently working."
        >
          <MoneyInput
            value={form.grossAnnualSalary ?? 0}
            onChange={v => setForm(f => ({ ...f, grossAnnualSalary: v }))}
            placeholder="0"
            aria-label="Gross annual salary"
          />
        </Field>

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row">
          <Button
            variant="ghost"
            onClick={onBack}
            block
            disabled={saving}
          >
            Back
          </Button>
          <Button
            onClick={onSubmit}
            block
            disabled={saving}
          >
            {saving ? 'Setting up…' : 'Continue → enter balances'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function parseIntSafe(s: string): number {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 0;
}
